import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { BadgeCheck, BookOpen, Download, FileText, Plus, Trash2, Upload } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  CERT_STANDARD_LABEL,
  PORTAL_DOCUMENT_KIND_LABEL,
  PortalDocumentKind,
  SUPPLIER_DOC_KIND_LABEL,
  UserRole,
  type PortalDocumentDto,
  type SupplierSelfDto,
} from '@weloze/shared';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useConfirm } from '../components/ConfirmDialog';
import { formatDate, formatDateTime } from '../lib/format';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Modal,
  Select,
  Skeleton,
} from '../components/ui';

/**
 * `/documentos` — o manual e os formulários RQ (PLAN §9, fatia i).
 *
 * São os mesmos para toda a carteira, e por isso o arquivo por trás não tem loja
 * dona. O download continua autenticado e continua registrado: saber quem baixou
 * o manual e quando é o que responde "o fornecedor conhecia a regra?".
 *
 * A ordem é a do uso: manual primeiro, formulários depois. Alfabética esconderia
 * o manual no meio dos RQ, e é ele que a maioria vem buscar.
 *
 * ## Os documentos da própria empresa também moram aqui (09/2026)
 *
 * Eles já apareciam em **Minha empresa**, no meio da ficha — junto de contatos,
 * RNCs, planos de ação e índices. Quem procura "o meu certificado" ou "a minha
 * LO" vem a Documentos, que é onde a palavra está escrita, e encontrava só os
 * formulários gerais.
 *
 * Não é duplicação de cadastro, e sim de leitura: o envio continua tendo um
 * lugar só — certificado em `/certificados`, documento de ficha pela Qualidade.
 * Aqui só se vê e se baixa, do mesmo endpoint que a ficha usa (`/stores/:id/self`),
 * então as duas telas não têm como divergir.
 */

const ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx,.xlsx,.zip';

const KIND_ICON = {
  MANUAL: BookOpen,
  FORM: FileText,
  OTHER: FileText,
} as const;

export function DocumentsPage() {
  const { user, currentStore } = useAuth();
  const isAdmin = user?.role === UserRole.WELOZE_ADMIN;
  const storeId = currentStore?.storeId ?? null;

  const [items, setItems] = useState<PortalDocumentDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [meus, setMeus] = useState<SupplierSelfDto | null>(null);
  const [publicando, setPublicando] = useState(false);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setError(null);
    try {
      setItems(await api.get<PortalDocumentDto[]>('/portal-documents'));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível carregar os documentos.');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * A ficha só é buscada quando há loja corrente — ou seja, para quem é
   * fornecedor. O papel Weloze enxerga a carteira inteira e não tem "a minha
   * empresa"; para ele, os documentos de um fornecedor estão na ficha dele.
   *
   * Falha em silêncio de propósito: não conseguir carregar os documentos da
   * própria empresa não pode esconder o Manual, que é o que quase todo mundo
   * vem buscar aqui.
   */
  useEffect(() => {
    if (!storeId) {
      setMeus(null);
      return;
    }
    let ativo = true;
    api
      .get<SupplierSelfDto>(`/stores/${storeId}/self`)
      .then((dados) => {
        if (ativo) setMeus(dados);
      })
      .catch(() => {
        if (ativo) setMeus(null);
      });
    return () => {
      ativo = false;
    };
  }, [storeId]);

  function despublicar(documento: PortalDocumentDto) {
    confirm.ask({
      title: 'Despublicar documento',
      body: (
        <>
          <p>
            <strong>{documento.title}</strong>
            {documento.reference ? ` · ${documento.reference}` : ''} sai da lista para todos os
            fornecedores.
          </p>
          <p>
            O arquivo continua no acervo e quem já baixou continua com a cópia — despublicar tira da
            vitrine, não retroage.
          </p>
        </>
      ),
      confirmLabel: 'Despublicar',
      onConfirm: async () => {
        await api.delete(`/portal-documents/${documento.id}`);
        await load();
      },
    });
  }

  /**
   * Agrupado por **título**, e não por tipo apenas.
   *
   * Publicar a 20ª edição do manual não apaga a 19ª — e não deveria: quem baixou
   * a antiga pode precisar conferir o que leu. Mas as duas lado a lado na lista
   * fazem o fornecedor baixar a errada.
   *
   * O título é a identidade do documento: "Manual do Fornecedor Weloze"
   * publicado duas vezes são duas versões da mesma coisa; "RQ 002-04" e "RQ 093"
   * são documentos diferentes. A mais recente fica em cima, as anteriores
   * recolhidas.
   *
   * Sem campo novo no banco de propósito: um `supersededAt` exigiria alguém
   * lembrar de marcá-lo, e "esqueceram de marcar" é um estado pior do que
   * "deduzimos pela data".
   */
  const porTipo = (Object.keys(PORTAL_DOCUMENT_KIND_LABEL) as PortalDocumentKind[])
    .map((kind) => {
      const doTipo = (items ?? []).filter((doc) => doc.kind === kind);
      const porTitulo = new Map<string, PortalDocumentDto[]>();
      for (const doc of doTipo) {
        const lista = porTitulo.get(doc.title);
        if (lista) lista.push(doc);
        else porTitulo.set(doc.title, [doc]);
      }
      // A lista já vem do servidor com a mais recente primeiro dentro do tipo,
      // então a primeira de cada título é a vigente.
      return {
        kind,
        docs: [...porTitulo.values()].map((versoes) => ({
          atual: versoes[0]!,
          anteriores: versoes.slice(1),
        })),
      };
    })
    .filter((grupo) => grupo.docs.length > 0);

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Documentos</h1>
          <p className="mt-1 text-sm text-slate-600">
            {storeId
              ? 'Os documentos da sua empresa, e o Manual do Fornecedor e os formulários da Qualidade na versão vigente.'
              : 'O Manual do Fornecedor e os formulários da Qualidade, sempre na versão vigente.'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setPublicando(true)}>
            <Plus className="size-4" aria-hidden />
            Publicar documento
          </Button>
        )}
      </header>

      {error && <Alert>{error}</Alert>}

      <MeusDocumentos dados={meus} />

      {!items ? (
        <div className="space-y-2">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-16" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          title="Nenhum documento publicado"
          description={
            isAdmin
              ? 'Publique o Manual do Fornecedor para que ele apareça aqui para toda a carteira.'
              : 'A Weloze ainda não publicou documentos neste espaço.'
          }
        />
      ) : (
        porTipo.map((grupo) => (
          <Card key={grupo.kind}>
            <h2 className="font-medium text-slate-900">
              {PORTAL_DOCUMENT_KIND_LABEL[grupo.kind]}
            </h2>
            <ul className="mt-3 divide-y divide-slate-100">
              {grupo.docs.map(({ atual: doc, anteriores }) => {
                const Icone = KIND_ICON[doc.kind];
                return (
                  <li key={doc.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-3">
                    <Icone className="size-4 shrink-0 text-slate-400" aria-hidden />
                    <div className="min-w-0 flex-1">
                      <p className="text-slate-800">
                        {doc.title}
                        {doc.reference && (
                          <span className="ml-2 text-sm text-slate-500">{doc.reference}</span>
                        )}
                      </p>
                      <p className="text-xs text-slate-500">
                        {doc.fileName} ·{' '}
                        {(doc.sizeBytes / 1024).toLocaleString('pt-BR', {
                          maximumFractionDigits: 0,
                        })}{' '}
                        kB · publicado em {formatDateTime(doc.publishedAt)}
                        {doc.publishedByName && ` por ${doc.publishedByName}`}
                      </p>
                    </div>

                    {/* Link para a rota autenticada, nunca para o arquivo em
                        disco: é ela que registra quem baixou. */}
                    <a
                      href={`/api/files/${doc.fileId}`}
                      className="flex items-center gap-1 text-sm text-brand-700 underline-offset-4 hover:underline"
                    >
                      <Download className="size-4" aria-hidden />
                      Baixar
                    </a>

                    {isAdmin && (
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Despublicar ${doc.title}`}
                        onClick={() => despublicar(doc)}
                      >
                        <Trash2 className="size-4" aria-hidden />
                      </Button>
                    )}

                    {anteriores.length > 0 && (
                      <details className="w-full">
                        <summary className="cursor-pointer text-xs text-slate-500 hover:text-slate-700">
                          {anteriores.length === 1
                            ? '1 versão anterior'
                            : `${anteriores.length} versões anteriores`}
                        </summary>
                        <ul className="mt-1 space-y-1 pl-4">
                          {anteriores.map((antiga) => (
                            <li
                              key={antiga.id}
                              className="flex flex-wrap items-center gap-2 text-xs text-slate-500"
                            >
                              <span>{antiga.reference ?? formatDateTime(antiga.publishedAt)}</span>
                              <a
                                href={`/api/files/${antiga.fileId}`}
                                className="text-brand-700 underline-offset-4 hover:underline"
                              >
                                baixar
                              </a>
                              {isAdmin && (
                                <button
                                  type="button"
                                  onClick={() => despublicar(antiga)}
                                  className="text-red-600 underline-offset-4 hover:underline"
                                >
                                  despublicar
                                </button>
                              )}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </li>
                );
              })}
            </ul>
          </Card>
        ))
      )}

      {confirm.dialog}

      <PublishModal
        open={publicando}
        onClose={() => setPublicando(false)}
        onDone={() => {
          setPublicando(false);
          void load();
        }}
      />
    </div>
  );
}

/**
 * A publicação, em dois passos.
 *
 * O arquivo sobe por `/portal-files`, que é a **única** porta que cria arquivo
 * sem loja dona — e é de admin. Depois o id vira documento. Um teste de
 * arquitetura falha se outra rota passar a criar arquivo sem dono.
 */
function PublishModal({
  open,
  onClose,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  onDone: () => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState({
    title: '',
    kind: 'FORM' as PortalDocumentKind,
    reference: '',
  });
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!file) {
      setFields({ file: 'Escolha o arquivo do documento.' });
      return;
    }
    setSaving(true);
    setError(null);
    setFields({});
    try {
      const enviado = await api.upload<{ id: string }>('/portal-files', file);
      await api.post('/portal-documents', {
        fileId: enviado.id,
        title: form.title,
        kind: form.kind,
        reference: form.reference || null,
      });
      onDone();
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.fields ? null : err.message);
        setFields(err.fields ?? {});
      } else {
        setError('Não foi possível publicar o documento.');
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Publicar documento">
      <form onSubmit={submit} className="space-y-4">
        {error && <Alert>{error}</Alert>}

        <Field
          label="Título"
          name="title"
          required
          placeholder="Manual do Fornecedor Weloze"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          error={fields.title}
        />

        <div className="flex flex-wrap gap-3">
          <Select
            label="Tipo"
            name="kind"
            className="w-56"
            value={form.kind}
            onChange={(e) => setForm({ ...form, kind: e.target.value as PortalDocumentKind })}
          >
            {(Object.keys(PORTAL_DOCUMENT_KIND_LABEL) as PortalDocumentKind[]).map((valor) => (
              <option key={valor} value={valor}>
                {PORTAL_DOCUMENT_KIND_LABEL[valor]}
              </option>
            ))}
          </Select>
          <Field
            label="Edição ou revisão"
            name="reference"
            className="min-w-[12rem] flex-1"
            placeholder="19ª edição"
            value={form.reference}
            onChange={(e) => setForm({ ...form, reference: e.target.value })}
          />
        </div>

        <div>
          <p className="text-sm font-medium text-slate-700">Arquivo</p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <input
              ref={input}
              type="file"
              accept={ACCEPT}
              className="sr-only"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
            />
            <Button type="button" variant="outline" size="sm" onClick={() => input.current?.click()}>
              <Upload className="size-4" aria-hidden />
              Escolher arquivo
            </Button>
            {file && <span className="text-sm text-slate-600">{file.name}</span>}
          </div>
          {fields.file && <p className="mt-1 text-sm text-red-600">{fields.file}</p>}
        </div>

        <p className="rounded-lg border border-amber-200 bg-amber-50/60 p-3 text-sm text-amber-900">
          Este documento fica visível para <strong>todos os fornecedores</strong> com acesso ao
          portal. Não publique aqui nada específico de um fornecedor.
        </p>
        <p className="text-xs text-slate-500">
          Publicar com um título que já existe cria uma <strong>nova versão</strong>: ela passa a ser
          a vigente e a anterior fica recolhida, sem sumir.
        </p>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={saving}>
            Publicar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Os documentos da própria empresa: certificados do SGQ e documentos de ficha.
 *
 * Separados em dois blocos, e não numa lista só, porque são coisas diferentes
 * com consequências diferentes — e misturá-los é o erro que o modelo existe
 * para evitar. O certificado move o ISQ; LO, RQ 093, CQI e termo de aceite não
 * entram em índice nenhum, têm validade e alerta próprios.
 *
 * Mostra e não edita. O envio do certificado é em "Meus certificados"; os
 * documentos de ficha são lançados pela Qualidade. Um segundo formulário aqui
 * seria o segundo lugar por onde o mesmo cadastro entra.
 */
function MeusDocumentos({ dados }: { dados: SupplierSelfDto | null }) {
  if (!dados) return null;

  const temAlgo = dados.certifications.length > 0 || dados.documents.length > 0;

  return (
    <Card>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-medium text-slate-900">Documentos da sua empresa</h2>
        <span className="font-mono text-xs text-slate-500">{dados.protheusRef}</span>
      </div>

      {!temAlgo ? (
        <p className="mt-2 text-sm text-slate-600">
          Nenhum documento cadastrado ainda. O certificado do seu sistema de gestão é enviado em{' '}
          <Link to="/certificados" className="text-brand-700 underline-offset-4 hover:underline">
            Meus certificados
          </Link>
          ; os demais são lançados pela Qualidade da Weloze.
        </p>
      ) : (
        <div className="mt-3 space-y-4">
          {dados.certifications.length > 0 && (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Certificados do sistema de gestão
              </h3>
              <ul className="mt-1 divide-y divide-slate-100">
                {dados.certifications.map((cert) => {
                  const vencido = cert.daysUntilExpiry < 0;
                  return (
                    <li
                      key={cert.id}
                      className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm"
                    >
                      <BadgeCheck className="size-4 shrink-0 text-slate-400" aria-hidden />
                      <span className="min-w-0 flex-1 text-slate-800">
                        {CERT_STANDARD_LABEL[cert.standard]}
                        {cert.number && (
                          <span className="ml-2 font-mono text-xs text-slate-500">
                            {cert.number}
                          </span>
                        )}
                      </span>
                      {cert.reviewStatus === 'PENDING' && (
                        <Badge tone="warning">aguardando validação</Badge>
                      )}
                      {cert.reviewStatus === 'REJECTED' && <Badge tone="danger">recusado</Badge>}
                      {cert.reviewStatus === 'APPROVED' && vencido && (
                        <Badge tone="danger">vencido</Badge>
                      )}
                      <span className="text-slate-600">
                        {vencido ? 'venceu em ' : 'até '}
                        {formatDate(cert.validUntil)}
                      </span>
                      {cert.fileId && <Baixar fileId={cert.fileId} />}
                    </li>
                  );
                })}
              </ul>
            </section>
          )}

          {dados.documents.length > 0 && (
            <section>
              <h3 className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Documentos da ficha
              </h3>
              <ul className="mt-1 divide-y divide-slate-100">
                {dados.documents.map((doc) => (
                  <li
                    key={doc.id}
                    className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm"
                  >
                    <FileText className="size-4 shrink-0 text-slate-400" aria-hidden />
                    <span className="min-w-0 flex-1 text-slate-800">
                      {SUPPLIER_DOC_KIND_LABEL[doc.kind]}
                      {doc.reference && (
                        <span className="ml-2 text-xs text-slate-500">{doc.reference}</span>
                      )}
                      {doc.cycleYear && (
                        <span className="ml-2 text-xs text-slate-500">ciclo {doc.cycleYear}</span>
                      )}
                    </span>
                    <span className="text-slate-600">
                      {doc.validUntil ? `até ${formatDate(doc.validUntil)}` : 'sem prazo'}
                    </span>
                    {doc.fileId ? (
                      <Baixar fileId={doc.fileId} />
                    ) : (
                      <span className="text-xs text-slate-400">sem arquivo</span>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </Card>
  );
}

/**
 * O link de download.
 *
 * Rota autenticada, e não link estático: é ela que confere a loja dona e grava
 * a linha em `file_accesses`. Um `<a>` para o disco pularia as duas coisas.
 */
function Baixar({ fileId }: { fileId: string }) {
  return (
    <a
      href={`/api/files/${fileId}`}
      className="inline-flex items-center gap-1 text-sm text-brand-700 underline-offset-4 hover:underline"
    >
      <Download className="size-4" aria-hidden />
      baixar
    </a>
  );
}
